import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import { logger } from '@/utils/logger'

import { ChoicesState } from '@/store/apis/Learn'

// 定数

const GRADE_NIL = 0
const MATERIAL_TYPE_NIL = 0
const SUBJECT_ID_NIL = ''

const MATERIAL_TYPE_KANJI_DRILL = 4

// State types

interface GradeChoiceNode {
  grade: number
  id: number
  text: string
  isSelected: boolean
}
type Grades = GradeChoiceNode[]
interface MaterialTypeChoiceNode {
  materialType: number
  id: number
  text: string
  isSelected: boolean
}
type MaterialTypes = MaterialTypeChoiceNode[]
interface SubjectChoiceNode {
  subjectId: string
  id: number
  text: string
  isSelected: boolean
}
type Subjects = SubjectChoiceNode[]

@Module({
  stateFactory: true,
  namespaced: true,
  name: 'pages/Learn',
})
export default class Learn extends VuexModule {
  // State

  private prevPath_: string = ''
  private grades_: Grades = []
  private materialTypes_: MaterialTypes = []
  private subjects_: Subjects = []

  // Getters

  get prevPath(): string {
    return this.prevPath_
  }

  get grades(): Grades {
    return this.grades_
  }

  get selectedGrade(): GradeChoiceNode | undefined {
    return this.grades_.find(e => e.isSelected)
  }

  get materialTypes(): MaterialTypes {
    return this.materialTypes_
  }

  get selectedMaterialType(): MaterialTypeChoiceNode | undefined {
    return this.materialTypes_.find(e => e.isSelected)
  }

  get subjects(): Subjects {
    return this.subjects_
  }

  get selectedSubject(): SubjectChoiceNode | undefined {
    return this.subjects_.find(e => e.isSelected)
  }

  get canOpenMaterialTypeMenu(): boolean {
    if (this.selectedGrade === undefined) {
      return false
    }
    return this.selectedGrade.grade !== GRADE_NIL
  }

  get canOpenSubjectMenu(): boolean {
    if (this.selectedMaterialType === undefined) {
      return false
    }
    return this.selectedMaterialType.materialType !== MATERIAL_TYPE_NIL
  }

  get isNoSubjectChoice(): boolean {
    return this.subjects_.length === 1 && this.subjects_[0].subjectId === SUBJECT_ID_NIL
  }

  get constMaterialTypeKanjiDrill(): number {
    return MATERIAL_TYPE_KANJI_DRILL
  }

  // Mutations
  @Mutation
  clearState() {
    this.prevPath_ = ''
    this.grades_ = []
    this.materialTypes_ = []
    this.subjects_ = []
  }

  @Mutation
  updatePrevPath(value: string) {
    this.prevPath_ = value
  }

  @Mutation
  updateSubjectList(payload: ChoicesState) {
    const subjectList: Subjects = [
      {
        subjectId: SUBJECT_ID_NIL,
        id: 0,
        text: '教科を選ぶ',
        isSelected: true,
      },
    ]
    if (
      this.materialTypes_.some(material => material.materialType === MATERIAL_TYPE_KANJI_DRILL && material.isSelected)
    ) {
      this.subjects_ = [
        {
          subjectId: 'L2',
          id: 0,
          text: '--',
          isSelected: true,
        },
      ]
    } else {
      this.subjects_ = subjectList.concat(
        payload.attendanceSubject.map((e, idx) => {
          return {
            subjectId: e.subjectId,
            id: idx + 1,
            text: e.name,
            isSelected: false,
          }
        })
      )
    }
  }

  @Mutation
  updateChoices(payload: ChoicesState) {
    const gradeList: Grades = [
      {
        grade: GRADE_NIL,
        id: 0,
        text: '学年を選ぶ',
        isSelected: true,
      },
    ]
    this.grades_ = gradeList.concat(
      payload.memberGrade.map((e, idx) => {
        return {
          grade: e,
          id: idx + 1,
          text: e.toString() + '年生',
          isSelected: false,
        }
      })
    )

    const subjectList: Subjects = [
      {
        subjectId: SUBJECT_ID_NIL,
        id: 0,
        text: '教科を選ぶ',
        isSelected: true,
      },
    ]
    this.subjects_ = subjectList.concat(
      payload.attendanceSubject.map((e, idx) => {
        return {
          subjectId: e.subjectId,
          id: idx + 1,
          text: e.name,
          isSelected: false,
        }
      })
    )

    this.materialTypes_ = [
      {
        materialType: MATERIAL_TYPE_NIL,
        id: 0,
        text: '教材を選ぶ',
        isSelected: true,
      },
      {
        materialType: 1,
        id: 1,
        text: '毎月の教材',
        isSelected: false,
      },
      {
        materialType: 3,
        id: 2,
        text: '実力テスト',
        isSelected: false,
      },
    ]
    // Tコースのみ漢字ドリルを追加する
    if (payload.courseId.startsWith('T')) {
      const newMaterialType = {
        materialType: MATERIAL_TYPE_KANJI_DRILL,
        id: 4,
        text: '漢字ドリル',
        isSelected: false,
      }

      // Find the index of the object with materialType: 1
      const index = this.materialTypes_.findIndex(material => material.materialType === 1)

      // Insert the new object after the object with materialType: 1
      this.materialTypes_.splice(index + 1, 0, newMaterialType)
    }
  }

  @Mutation
  changeSelectedGrade(gradeId: number) {
    this.grades_.forEach(grade => {
      if (grade.id === gradeId) {
        grade.isSelected = true
      } else {
        grade.isSelected = false
      }
    })
  }

  @Mutation
  changeSelectedMaterialType(materialTypeId: number) {
    this.materialTypes_.forEach(materialType => {
      if (materialType.id === materialTypeId) {
        materialType.isSelected = true
      } else {
        materialType.isSelected = false
      }
    })
  }

  @Mutation
  changeSelectedSubject(subjectId: number) {
    this.subjects_.forEach(subject => {
      if (subject.id === subjectId) {
        subject.isSelected = true
      } else {
        subject.isSelected = false
      }
    })
  }

  // Actions

  @Action
  clear() {
    this.context.commit('clearState')
  }

  @Action
  setPrevPath(param: string) {
    logger.trace('in: setPrevPath')

    this.context.commit('updatePrevPath', param)
  }

  // プルダウンの状態を初期化する
  @Action
  refreshChoices(param: ChoicesState) {
    logger.trace('in: refreshChoices')

    this.context.commit('updateChoices', param)
  }

  @Action
  refreshSubjectList(param: ChoicesState) {
    logger.trace('in: updateSubjectList')

    this.context.commit('updateSubjectList', param)
  }

  // 学年プルダウンを確定
  @Action
  chooseGrade(param: number) {
    logger.trace('in: chooseGrade')

    this.context.commit('changeSelectedGrade', param)
  }

  // 教材種別プルダウンを確定
  @Action
  chooseMaterialType(param: number) {
    logger.trace('in: chooseMaterialType')

    this.context.commit('changeSelectedMaterialType', param)
  }

  // 教科プルダウンを確定
  @Action
  chooseSubject(param: number) {
    logger.trace('in: chooseSubject')

    this.context.commit('changeSelectedSubject', param)
  }

  // 教材種別プルダウンを初期値にリセット
  @Action
  resetMaterialType() {
    logger.trace('in: resetMaterialType')

    this.context.commit('changeSelectedMaterialType', 0)
  }

  // 教科プルダウンを初期値にリセット
  @Action
  resetSubject() {
    logger.trace('in: resetSubject')

    this.context.commit('changeSelectedSubject', 0)
  }
}
